<template>
  <div class="info-card tides-card">
    <el-collapse-item :name="props.topic.id">
      <template #title>
        <span class="title-container">
          <span class="title-text">{{ translation.title }}</span>
        </span>
      </template>

      <div v-if="data.errorMessage && !data.isLoading" class="no-data">
        {{ data.errorMessage }}
      </div>

      <section v-else class="tides-container" v-loading="data.isLoading">
        <p class="padding-top-0">{{ helperStore.i18n('tide_select_location') }}</p>

        <el-select
          :model-value="data.selectedStation"
          @change="changeStation"
          value-key="name"
          class="station-select"
          fit-input-width
          :teleported="false"
        >
          <el-option
            v-for="station in data.tidesData.stations"
            :key="station.id"
            :value="station"
            :label="station.name"
          />
        </el-select>

        <el-row type="flex" align="middle" justify="space-between">
          <p class="label">{{ helperStore.i18n('next_tide') }}</p>
          <i class="icon-Reload" @click="getTides(true)"></i>
        </el-row>

        <el-row type="flex" justify="center" align="middle">
          <el-col :span="12" class="text--large">
            {{ getHeight(nextTide.height) }}
            {{ data.isMetricHeight ? 'm' : 'ft' }}
          </el-col>

          <el-col :span="12" class="text--large">{{ nextTideTime }}</el-col>
        </el-row>

        <el-row type="flex" justify="center" align="middle">
          <el-col :span="12" class="text">
            {{ helperStore.i18n(nextTide.type === 'High' ? 'high_tide' : 'low_tide') }}
          </el-col>
          <el-col :span="12" class="text">{{ getDate(nextTide.date) }}</el-col>
        </el-row>

        <!-- Daily Tides -->
        <div class="tides-table">
          <el-row
            class="tides-table__header"
            v-if="data.tidesData.tides"
            type="flex"
            align="middle"
          >
            <el-select
              :model-value="data.selectedTideDate"
              value-key="date"
              @change="onTideDateChange"
              fit-input-width
            >
              <el-option
                v-for="extreme in tideExtremes"
                :key="extreme.date"
                :value="extreme.date"
                :label="getDate(extreme?.date)"
              />
            </el-select>
          </el-row>

          <el-row
            class="tides-table__row"
            v-for="tide in currentExtremeShown?.values"
            :key="tide.date"
            type="flex"
            align="middle"
          >
            <el-col :span="6">
              {{ getTime(tide.date) }}
            </el-col>

            <el-col :span="8">
              {{ helperStore.i18n(tide.type === 'High' ? 'high_tide' : 'low_tide') }}
            </el-col>

            <el-col :span="8">
              {{ getHeight(tide.height) }} {{ data.isMetricHeight ? 'm' : 'ft' }}
            </el-col>
          </el-row>
        </div>

        <!-- Switches -->
        <el-row class="switch-row" type="flex" align="middle">
          <i class="icon-Clock"></i>

          <div class="switch">
            <div
              @click="setSwitchValue('is24HDay', false)"
              :class="{ active: !data.is24HDay }"
              :style="{ backgroundColor: !data.is24HDay ? theme.accentColor : 'inherit' }"
            >
              12
            </div>

            <div
              @click="setSwitchValue('is24HDay', true)"
              :class="{ active: data.is24HDay }"
              :style="{ backgroundColor: data.is24HDay ? theme.accentColor : 'inherit' }"
            >
              24
            </div>
          </div>

          <i class="icon-Swap"></i>

          <div class="switch">
            <div
              @click="setSwitchValue('isMetricHeight', true)"
              :class="{ active: data.isMetricHeight }"
              :style="{ backgroundColor: data.isMetricHeight ? theme.accentColor : 'inherit' }"
            >
              m
            </div>

            <div
              @click="setSwitchValue('isMetricHeight', false)"
              :class="{ active: !data.isMetricHeight }"
              :style="{ backgroundColor: !data.isMetricHeight ? theme.accentColor : 'inherit' }"
            >
              ft
            </div>
          </div>
        </el-row>
      </section>

      <el-row justify="end">
        <div class="info-card__btn" @click="onShareClick" :style="{ backgroundColor: theme.accentColor }">
          <i class="icon-Share-3"></i>
        </div>
      </el-row>
    </el-collapse-item>
  </div>
</template>

<script lang="ts" setup>
import api from '@/api'
import { computed, onMounted, reactive, watch } from 'vue'
import { ElNotification } from 'element-plus'
import { ShareTypesEnum, Topic } from '@/interfaces/guide.interface'
import { TideExtreme, TidesResponse, TideStation, TideValue } from '@/interfaces/api.interface'
import dayjs from 'dayjs'
import { DAYJS_LOCALES } from '@/utils/consts'
import { LanguageCodesEnum } from '@/interfaces/languages.interface'
import { useHelperStore } from '@/pinia/helper.store'

const TIDE_DATE_FORMAT = 'ddd D MMM YYYY'
const TIDE_12H_TIME_FORMAT = 'hh:mm a'
const TIDE_24H_TIME_FORMAT = 'HH:mm'
const M_TO_F_RATIO = 3.280839895

const helperStore = useHelperStore()
const emit = defineEmits<{
  (e: 'show-share-overlay', payload: { type: ShareTypesEnum; topicId: number }): void
}>()
const props = defineProps<{ topic: Topic; isTopicExpanded: boolean }>()
const data = reactive({
  isLoading: false,
  errorMessage: null,
  tidesData: {} as TidesResponse,
  selectedStation: null as TideStation,
  isMetricHeight: true,
  is24HDay: true,
  selectedTideDate: ''
})

const translation = computed(() => props.topic.translations[helperStore.currentLanguage])
const theme = computed(() => helperStore.styleData)

const currentTimeFormat = computed(() =>
  data.is24HDay ? TIDE_24H_TIME_FORMAT : TIDE_12H_TIME_FORMAT
)
const nextTideTime = computed(() => dayjs(nextTide.value.date).format(currentTimeFormat.value))
const tideExtremes = computed(() => data.tidesData?.tides?.extremes)
const currentExtremeShown = computed<TideExtreme>(() =>
  tideExtremes.value?.find((e) => getDate(e.date) === data.selectedTideDate)
)
const nextTide = computed<TideValue>(() => tideExtremes.value?.[0]?.values[0] || ({} as TideValue))

watch(
  () => props.isTopicExpanded,
  async (isExpanded: boolean) => {
    if (isExpanded && !data.tidesData.tides) {
      await getTides(false)
    }
  }
)

watch(
  () => helperStore.currentLanguage,
  (newLanguage) => {
    setLocale(newLanguage)
  }
)

onMounted(async () => {
  await setLocale(helperStore.currentLanguage)

  try {
    if (props.isTopicExpanded) {
      await getTides()
    }
  } catch (e) {
    console.error(e)
  }
})

async function setLocale(language: LanguageCodesEnum) {
  if (DAYJS_LOCALES[language]) {
    await DAYJS_LOCALES[language]()
    dayjs.locale(language)
  }
}

function getTime(timestamp: string): string {
  return dayjs(timestamp).format(currentTimeFormat.value)
}

function getDate(date: string): string {
  return date && dayjs(date).format(TIDE_DATE_FORMAT)
}

function getHeight(value: number) {
  return data.isMetricHeight ? value?.toFixed(2) : (value * M_TO_F_RATIO)?.toFixed(2)
}

function onTideDateChange(date: string) {
  data.selectedTideDate = getDate(date)
}

async function changeStation(station: TideStation) {
  data.selectedStation = station
  await getTides()
}

async function getTides(isManualRefresh = false) {
  const LS_KEY = 'tsTidesLastFetched'
  const REQUEST_FREQUENCY_LIMIT = 60 * 60 * 1000 // 1 hour

  const lastTimeFetched = +localStorage.getItem(LS_KEY)
  const isNotAllowedToFetch = Date.now() - lastTimeFetched <= REQUEST_FREQUENCY_LIMIT

  if (isManualRefresh && lastTimeFetched && isNotAllowedToFetch) {
    ElNotification.info({
      title: helperStore.i18n('tides_refresh_title'),
      message: helperStore.i18n('tides_refresh_message'),
      duration: 5000
    })

    return
  }

  try {
    data.isLoading = true
    let params = {}

    if (data.selectedStation) {
      params = {
        lat: data.selectedStation.lat,
        lon: data.selectedStation.lon
      }
    }

    data.tidesData = await api.getTides(helperStore.guideToken, params)
    data.selectedStation = data.tidesData.stations.find(
      (s) => s.name === data.tidesData.tides.station
    )

    data.selectedTideDate = getDate(data.tidesData.tides.extremes[0].date)
    data.is24HDay = data.tidesData.preferred_time_unit === '24hr'
    data.isMetricHeight = data.tidesData.preferred_height_unit === 'meters'
    data.isLoading = false

    localStorage.setItem(LS_KEY, Date.now().toString())
  } catch (e) {
    console.error(e)
    data.isLoading = false

    if (e && e.includes && e.includes('timeout')) {
      data.errorMessage =
        "Oops, the tides aren't loading because the current internet connection is poor. " +
        'Please try again once you have a better connection.'
    }
    if (e?.data?.error) {
      data.errorMessage = e.data.error
    }
  }
}

function setSwitchValue(type: 'isMetricHeight' | 'is24HDay', value: boolean): void {
  data[type] = value
}

function onShareClick() {
  emit('show-share-overlay', { type: ShareTypesEnum.TOPIC, topicId: props.topic.id })
}
</script>

<style lang="scss" scoped>
.tides-card {
  p {
    margin: 0;
    padding: 20px 0;

    &.padding-top-0 {
      padding-top: 0;
    }
  }

  :deep(.station-select .el-select__wrapper) {
    width: 100%;
    box-shadow: $info-block-shadow;
    height: 40px;
    font-family: v-bind('helperStore.headerFont');

    i {
      font-size: 1rem;
    }
  }

  p {
    font-size: 1rem;
  }

  .text {
    font-size: 1rem;
    text-align: center;
    padding: 10px 0;
  }

  .text--large {
    font-size: 2.25rem;
    line-height: 2.25rem;
    text-align: center;
    letter-spacing: 0;
  }
}

.title-text {
  color: inherit;
  font-family: v-bind('helperStore.headerFont');
}

.no-data {
  padding: 15px;
  font-size: 0.875rem;
}

.tides-container {
  padding: 0 14px;
  font-size: 1rem;
  z-index: 9999;

  .icon-Reload {
    font-size: 1.5rem;
    font-weight: bold;
    cursor: pointer;
  }
}

i {
  cursor: default;
}

.switch-row {
  i {
    padding: 0 10px;

    &:first-child {
      padding-left: 0;
    }
  }

  .switch {
    border-radius: $border-radius * 2;
    background-color: #c4c4c4;
    border: 0.5px solid #6d6d6d;
    font-size: 0.875rem;
    overflow: hidden;
    display: flex;

    div {
      cursor: pointer;
      display: inline-flex;
      justify-content: center;
      padding: 0 $padding;
      width: 50%;
      transition: all 0.3s ease-in-out;

      &.active {
        color: white;
      }

      &:first-child {
        border-right: 1px solid #6d6d6d;
      }
    }
  }
}

.tides-table {
  width: 100%;
  padding-bottom: 20px;

  &__header {
    background-color: v-bind('theme.accentColor');
    margin: 0 -15px;
    height: 40px;

    :deep(.el-select) {
      margin: 0 15px;
      max-width: 180px;

      i {
        font-size: 1rem;
      }
    }
  }

  &__row {
    height: 40px;
    margin: 0 -14px;
    padding: 0 14px;
    border-bottom: 1px solid $border-color;
  }
}

.no-data {
  font-size: 1rem;
  text-align: center;
}
</style>
